import {
  ChevronLeft,
  Clock,
  Copy01,
  Edit01,
  SearchLg,
} from '@untitledui/icons';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { QueryErrorState } from '@/lib/components';
import { getTypeLabel } from '@/lib/crm/utils/getTypeLabel';
import { useOrgTimeFormat } from '@/lib/time';
import type { AssociationDetail, CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { EntityBadgeGroup } from '../../components/badges/EntityBadgeGroup';
import type { EntityBadgeChangeMarker } from '../../components/badges/types';
import { JsonPropertyReadFields } from '../../components/properties/JsonPropertyFields';
import { AssociationDiffValue } from '../../components/properties/labels/AssociationDiffValue';
import { CopyAffordanceContext } from '../../components/properties/labels/copyAffordance';
import { ExpandableMultiValueLabel } from '../../components/properties/labels/ExpandableMultiValueLabel';
import { ExpandablePropertyValue } from '../../components/properties/labels/ExpandablePropertyValue';
import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '../../core/SchemaClient';
import { useAssociationFieldSubsets } from '../../hooks/useAssociationFieldSubsets';
import { useAssociations } from '../../hooks/useAssociations';
import { useAssociationTypes } from '../../hooks/useAssociationTypes';
import { useEntityTypeSchema } from '../../hooks/useEntityTypeSchema';
import { useObjectTypes } from '../../hooks/useObjectTypes';
import { usePipelineStages } from '../../hooks/usePipelineStages';
import { usePropertySchema } from '../../hooks/usePropertySchema';
import { useUpdateCrmAssociation } from '../../hooks/useUpdateCrmAssociation';
import { useUpdateCrmProperty } from '../../hooks/useUpdateCrmProperty';
import {
  type DetailField,
  detailFieldKey,
} from '../../record/registry/entityTypeRegistry';
import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';
import { PropertyDiffValue } from '../../record/timeline/components/shared/PropertyDiffValue';
import { isAssociationValueFrozen } from '../../utils/associationEditability';
import { buildEntityKindMap } from '../../utils/associationMatching';
import { getAssociationTargetIconComponent } from '../../utils/icons/EntityTypeIcon';
import { getPropertyTypeIconComponent } from '../../utils/icons/PropertyTypeIcon';
import {
  hasPropertyEditor,
  isPropertyEditable,
} from '../../utils/propertyEditability';
import { getStageEntityId } from '../../utils/propertyHelpers';
import { EmptyFieldPlaceholder } from '../components/EmptyFieldPlaceholder';
import { InlineEditableFieldRow } from '../components/InlineEditableFieldRow';
import { OverviewFieldRow } from '../components/OverviewFieldRow';
import { RowActionButton } from '../components/RowActionButton';
import { useRecordDraft } from '../contexts/RecordDraftContext';
import type { SaveBarState } from '../types';
import {
  buildDetailFieldList,
  expandKindBasedAssociationFields,
  isEmptyValue,
} from '../utils/buildDetailFieldList';
import {
  isHiddenAssociation,
  passesOverviewKindFilter,
} from '../utils/buildFallbackFieldList';
import { getCreateFormFieldKeys } from '../utils/createFormFieldKeys';
import {
  associationSearchText,
  matchesSearch,
  propertyValueSearchText,
} from '../utils/fieldSearchText';
import { getRowCopyText } from '../utils/rowCopyText';
import { formatTimestamp, getTimestampLabel } from '../utils/timestampHelpers';
import { CollapsibleSection } from './collapsible-section';
import { EmailThreadOverviewEnrichment } from './email-thread-overview-enrichment';
import { MeetingNotesOverviewEnrichment } from './meeting-notes-overview-enrichment';
import { MeetingOverviewEnrichment } from './meeting-overview-enrichment';
import { OverviewEditSection } from './overview-edit-section';
import { SectionActionButton } from './section-action-button';

/** Hoisted so the no-draft path keeps a stable identity across renders. */
/** Stable empty fallbacks so absent data doesn't churn memos / row props. */
const EMPTY_TYPES: CrmObjectTypeSchema[] = [];
const EMPTY_PREVIOUS_VALUES: Record<string, unknown> = {};
const EMPTY_ASSOCIATION_CHANGES: Record<string, EntityBadgeChangeMarker> = {};
const EMPTY_KEY_SET: ReadonlySet<string> = new Set();
/** Stable empty list so the pre-schema render doesn't re-trigger hydration. */
const EMPTY_ASSOCIATIONS: AssociationDetail[] = [];

interface OverviewSectionProps {
  object: CrmObject;
  onSaveStateChange?: (state: SaveBarState | null) => void;
  /**
   * When false, the overview is not collapsible (no disclosure chevron) — used
   * on the full-page record where hiding the overview is pointless.
   * @default true
   */
  collapsible?: boolean;
  /** Extra control rendered at the far right of the section header row. */
  headerTrailing?: ReactNode;
  /**
   * Section heading override. Defaults to `${entityTypeLabel} overview`; the
   * automation slideout's Settings tab passes 'Settings' so the section
   * heading agrees with the tab that contains it.
   */
  title?: string;
  /**
   * When true, render no section header at all — the rows flow directly under
   * whatever sits above (the automation Overview tab drops its 'Settings'
   * heading this way). The header's controls keep working: the Edit/Cancel
   * action renders right-aligned in a slim row above the fields, and the
   * "View all values" search swaps into that row. Header-only affordances
   * (collapse chevron, `headerTrailing`) don't render in this mode.
   */
  hideHeader?: boolean;
}

/**
 * Section wrapper that is either the normal CollapsibleSection or — in
 * `hideHeader` mode — a plain block with a slim right-aligned action row
 * (Edit/Cancel, or the view-all search when active) above the children.
 * Hoisted to module scope for a stable identity across renders.
 */
function OverviewSectionShell({
  hideHeader,
  title,
  actions,
  headerOverride,
  collapsible,
  headerTrailing,
  children,
}: {
  hideHeader: boolean;
  title: string;
  actions?: ReactNode;
  headerOverride?: ReactNode;
  collapsible: boolean;
  headerTrailing?: ReactNode;
  children: ReactNode;
}) {
  if (!hideHeader) {
    return (
      <CollapsibleSection
        title={title}
        defaultExpanded
        actions={actions}
        collapsible={collapsible}
        headerTrailing={headerTrailing}
        headerOverride={headerOverride}
      >
        {children}
      </CollapsibleSection>
    );
  }
  return (
    <div>
      {(headerOverride || actions) && (
        <div
          className={`flex min-h-9 items-center gap-2 ${
            headerOverride ? '' : 'justify-end'
          }`}
        >
          {headerOverride ?? <div className='shrink-0'>{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

interface CopyButtonProps {
  text: string;
  label: string;
  onCopy: (text: string, label: string) => void;
}
// Lives in the row's action cluster (pencil · clear · copy) on an editable
// row, so it shares RowActionButton's reveal and sizing with its neighbours.
function CopyButton({ text, label, onCopy }: CopyButtonProps) {
  const handleClick = useCallback(
    () => onCopy(text, label),
    [onCopy, text, label]
  );
  return (
    <RowActionButton
      icon={Copy01}
      label={`Copy ${label}`}
      onClick={handleClick}
    />
  );
}

export function OverviewSection({
  object,
  onSaveStateChange,
  collapsible = true,
  headerTrailing,
  title: titleOverride,
  hideHeader = false,
}: OverviewSectionProps) {
  const orgId = object.org_id;
  const entityTypeName = object.entity_type_name;
  const config = getEntityTypeConfig(entityTypeName);
  const { schema: entityTypeSchema } = useEntityTypeSchema(
    orgId,
    entityTypeName
  );
  const { timeFormat } = useOrgTimeFormat();

  const [isEditing, setIsEditing] = useState(false);
  // "View all values": the panel grows from what's filled to everything the
  // record type can hold, and the header becomes a search field over it.
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const viewAllButtonRef = useRef<HTMLButtonElement>(null);
  const toggleShowAll = useCallback(() => {
    setQuery('');
    setShowAll(prev => !prev);
  }, []);
  // Leaving the expanded view hands focus back to the toggle the user
  // started from (without scrolling to it — a mouse user shouldn't see
  // the panel jump for a focus they didn't ask for).
  const collapseAll = useCallback(() => {
    setQuery('');
    setShowAll(false);
    viewAllButtonRef.current?.focus({ preventScroll: true });
  }, []);
  // Escape clears the search first, then leaves the expanded view. The
  // field's own keyboard handling contains the key, so it never reaches
  // the slideout and closes the record.
  const handleSearchKeyDown = useCallback(
    (e: { key: string; preventDefault: () => void }) => {
      if (e.key !== 'Escape') {
        return;
      }
      e.preventDefault();
      if (query) {
        setQuery('');
        return;
      }
      collapseAll();
    },
    [query, collapseAll]
  );

  // Copy a field's value to the clipboard (revealed on row hover). Guards the
  // Clipboard API (undefined in insecure contexts / older browsers) so it
  // never throws synchronously before the promise chain can catch.
  const handleCopyValue = useCallback((text: string, label: string) => {
    if (!navigator.clipboard?.writeText) {
      toast.error('Could not copy to clipboard');
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`Copied ${label.toLowerCase()}`))
      .catch(() => toast.error('Could not copy to clipboard'));
  }, []);
  const handleExitEdit = useCallback(() => setIsEditing(false), []);
  // Stable no-op to avoid re-render cascades when onSaveStateChange is not provided
  const noopSaveState = useCallback((_state: SaveBarState | null) => {}, []);
  const { hasPermission } = usePermissions();

  // A draft is a proposal projected onto this record. `null` on a live record,
  // which is what keeps every existing surface on its current path.
  const draft = useRecordDraft();
  const draftPreviousValues = draft?.previousValues ?? EMPTY_PREVIOUS_VALUES;
  // Association counterpart of `draftPreviousValues` — marks each badge with
  // what the proposal does to it rather than implying a replacement.
  const draftAssociationChanges =
    draft?.associationChanges ?? EMPTY_ASSOCIATION_CHANGES;

  const canEdit =
    !config.readOnly &&
    hasPermission(entityTypeName, 'ahoy_property_updated') &&
    // Inline editing is suppressed while a draft is on screen. The chat is the
    // editor in that mode, and allowing both would mean reconciling two edit
    // sources over the same fields. It also keeps the property-update path away
    // from a record that may not exist on the server yet.
    draft === null;

  // Association counterpart to canEdit: shares the same registry/draft
  // suppression (a read-only type or a projected draft shouldn't offer
  // inline association editing either) but checks its own permission pair —
  // ahoy_property_updated does not cover ahoy_association_added/removed.
  // Per-field write-access and frozen-value checks happen at render time.
  const canEditAssociations =
    !config.readOnly &&
    draft === null &&
    hasPermission(entityTypeName, 'ahoy_association_added') &&
    hasPermission(entityTypeName, 'ahoy_association_removed');

  const { user } = useAuthInfo();
  const { mutateAsync: updateCrmPropertyAsync } = useUpdateCrmProperty(
    orgId,
    user?.id
  );
  const { mutateAsync: updateCrmAssociationAsync } = useUpdateCrmAssociation(
    orgId,
    user?.id
  );

  // Header "Edit" switch deferral: entering full edit mode unmounts every
  // inline row immediately (see the isEditing early return below), which
  // would abandon a save mid-flight. inFlightInlineSavesRef counts pending
  // inline saves; a click on "Edit" while it's non-zero queues the switch
  // instead of firing it. settleInlineSave resolves the queued switch once
  // the last save settles — open on success, stay put (the row already shows
  // its own error) if any save in the batch failed.
  const inFlightInlineSavesRef = useRef(0);
  const pendingEditSwitchRef = useRef(false);
  const pendingSwitchFailedRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const settleInlineSave = useCallback((succeeded: boolean) => {
    inFlightInlineSavesRef.current = Math.max(
      0,
      inFlightInlineSavesRef.current - 1
    );
    if (pendingEditSwitchRef.current && !succeeded) {
      pendingSwitchFailedRef.current = true;
    }
    if (inFlightInlineSavesRef.current === 0 && pendingEditSwitchRef.current) {
      pendingEditSwitchRef.current = false;
      const shouldOpen = !pendingSwitchFailedRef.current;
      pendingSwitchFailedRef.current = false;
      // Unrelated unmount guard: the slideout may have closed (or navigated
      // away) while this save was in flight — never switch modes after that.
      if (shouldOpen && isMountedRef.current) {
        setIsEditing(true);
      }
    }
  }, []);

  // Field keys cleared inline this record's mount lifetime. Read mode drops
  // empty values (buildDetailFieldList's isEmptyValue checks for properties;
  // the objects.length filter below for associations), so without this the
  // row the user just cleared would vanish out from under the cursor instead
  // of staying click-to-edit and retypeable. Reset (via the render-time
  // pattern, not an Effect) whenever the displayed entity changes so a
  // cleared field never leaks onto a different record.
  const [clearedPropertyKeys, setClearedPropertyKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [clearedAssociationKeys, setClearedAssociationKeys] = useState<
    Set<string>
  >(() => new Set());
  const [clearedForEntityId, setClearedForEntityId] = useState(
    object.entity_id
  );
  if (object.entity_id !== clearedForEntityId) {
    setClearedForEntityId(object.entity_id);
    setClearedPropertyKeys(new Set());
    setClearedAssociationKeys(new Set());
    // A different record starts from what's filled, not mid-search.
    setShowAll(false);
    setQuery('');
  }

  const handleSaveProperty = useCallback(
    async (propertyName: string, value: unknown): Promise<void> => {
      inFlightInlineSavesRef.current += 1;
      // Mark the row as cleared BEFORE the save: the update lands
      // optimistically, so by the time the promise resolves the field list
      // has already been rebuilt without the (now empty) value and the row
      // has unmounted — it then reappeared a beat later when the mark
      // caught up. Marking first keeps the row in place throughout; if the
      // save fails the value is still there, so the extra key is inert.
      if (isEmptyValue(value)) {
        setClearedPropertyKeys(prev =>
          prev.has(propertyName) ? prev : new Set(prev).add(propertyName)
        );
      }
      try {
        await updateCrmPropertyAsync({ object, propertyName, value });
        settleInlineSave(true);
      } catch (err) {
        settleInlineSave(false);
        throw err;
      }
    },
    [object, updateCrmPropertyAsync, settleInlineSave]
  );

  const {
    schema,
    loading: schemaLoading,
    error: schemaError,
    refetch: refetchSchema,
  } = usePropertySchema(orgId, entityTypeName);

  // Pipeline-stages and association-types intentionally don't expose
  // `isLoading` to this section any more — they fill in inline (stage
  // badges, association labels) rather than gating the whole section on
  // a skeleton.
  //
  // We do still track assoc-types loading state for one specific purpose:
  // gating association *row rendering* (not the whole section) until the
  // access map is populated. Without this, an association whose type is
  // marked `internal` could briefly render before `assocAccessMap` is
  // ready — the access filter in `buildDetailFieldList` fails open when
  // the map is empty (`undefined !== 'internal'`).
  const {
    associationTypes,
    associationTypeMap,
    isLoading: assocTypesLoading,
  } = useAssociationTypes(orgId);

  const objectTypesQuery = useObjectTypes(orgId);
  const allObjectTypes = objectTypesQuery.data?.allObjectTypes ?? EMPTY_TYPES;

  const entityKindMap = useMemo(
    () => buildEntityKindMap(allObjectTypes),
    [allObjectTypes]
  );

  const assocTypeByName = useMemo(() => {
    const m = new Map<string, AssociationType>();
    for (const at of associationTypes) {
      m.set(at.association_type_name, at);
    }
    return m;
  }, [associationTypes]);

  // Only hydrate the association targets this section will actually render.
  //
  // `useAssociations` fires `ensureEntity` for EVERY target it is handed, which
  // fans out into batched /by-entity-ids/ POSTs (100 ids each). Handing it the
  // raw list means a high-degree record — a contact who is also a user, whose
  // graph carries every meeting and email thread they took part in — hydrates
  // hundreds of objects that `visibleFields` then discards, because their target
  // kind is activity-like. Observed in production: 738 associations hydrated
  // (~2.6 MB over 8 parallel POSTs) to render 4 rows.
  //
  // The same two gates the row builder applies (`appendVisibleAssociations`),
  // so this can only ever be a superset of what renders — never narrower.
  // Empty until the schema maps load, which is also when association rows are
  // gated off (`associationsAccessReady` below), so nothing renders unhydrated.
  // Registry-hidden associations never render as field rows (another surface
  // owns them — see EntityTypeConfig.hiddenAssociations), so their targets
  // are also excluded from hydration here.
  const hiddenAssociationSet = useMemo(
    () => new Set(config.hiddenAssociations ?? []),
    [config.hiddenAssociations]
  );

  const overviewAssociationEdges = useMemo(() => {
    const edges = object.associations ?? [];
    if (assocTypeByName.size === 0 || entityKindMap.size === 0) {
      return EMPTY_ASSOCIATIONS;
    }
    return edges.filter(
      a =>
        !hiddenAssociationSet.has(a.association_type_name) &&
        // WORKAROUND until #12411: excludes ahoy_task (and ahoy_note, ahoy_file,
        // ahoy_meeting, ...) via HIDDEN_ASSOCIATION_TARGETS — those own their
        // own section/tab and must not also render as an Overview field row.
        !isHiddenAssociation(a.association_type_name, associationTypeMap) &&
        passesOverviewKindFilter(
          a.association_type_name,
          assocTypeByName,
          entityKindMap
        )
    );
  }, [
    object.associations,
    assocTypeByName,
    entityKindMap,
    associationTypeMap,
    hiddenAssociationSet,
  ]);

  const {
    associations,
    isLoading: associationsLoading,
    error: associationsError,
    hasPartialError: associationsPartialError,
    refetch: refetchAssociations,
  } = useAssociations(orgId, overviewAssociationEdges, {
    sourceEntityTypeName: entityTypeName,
  });

  const { getStageByEntityId, stageTypeNames } = usePipelineStages(
    orgId,
    object.entity_type_name
  );

  // Label map: association_type_name → display label
  const assocLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const assocType of associationTypes) {
      map.set(assocType.association_type_name, assocType.label);
    }
    return map;
  }, [associationTypes]);

  // Access map: association_type_name → value_read_access
  const assocAccessMap = useMemo(() => {
    const map = new Map<string, 'all' | 'internal'>();
    for (const assocType of associationTypes) {
      map.set(assocType.association_type_name, assocType.value_read_access);
    }
    return map;
  }, [associationTypes]);

  // Resolved targets grouped by association type, then by the entity type each
  // target actually resolved to. A kind-based rule renders one field per type
  // (see associationTargetSubsets.ts), so a contact's tasks are their own field
  // — hidden here because the Tasks section owns them — rather than sitting
  // unlabelled among its deals. Shared with overview-edit-section.tsx via
  // useAssociationFieldSubsets so the read and edit surfaces agree on these
  // lookups by construction.
  const { presentTargetTypesByAssoc, targetsForField } =
    useAssociationFieldSubsets(associations, { includeReadExtras: true });

  /** entity_type_name -> its plural label, for naming a decomposed field. */
  const typeLabelPluralMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of allObjectTypes) {
      map.set(t.entity_type_name, t.label_plural || t.label);
    }
    return map;
  }, [allObjectTypes]);

  // Latest-object ref for handleSaveAssociation: the mutation only needs
  // entity_id/entity_type_name off `object` (it re-fetches fresh data itself
  // — see useUpdateCrmAssociation), so reading it here instead of closing over
  // `object` keeps the callback's identity — and the fieldSavers/renderedIds
  // closures built from it — stable across every association-store update,
  // rather than recreating on every new `object` reference.
  const objectRef = useRef(object);
  useEffect(() => {
    objectRef.current = object;
  });

  const handleSaveAssociation = useCallback(
    async (
      associationTypeName: string,
      value: unknown,
      targetEntityTypeName?: string,
      knownSubsetTargetIds?: string[]
    ): Promise<void> => {
      let pickedIds: string[];
      if (value === null || value === undefined) {
        pickedIds = [];
      } else if (Array.isArray(value)) {
        pickedIds = value as string[];
      } else {
        pickedIds = [value as string];
      }
      // When this field renders one subset of a kind-based rule, the picker's
      // value is complete for THAT TYPE only — the scope is passed through so
      // the write stays a delta within it and cannot touch the record's other
      // subsets (its tasks, say).
      const fieldKey = targetEntityTypeName
        ? `${associationTypeName}::${targetEntityTypeName}`
        : associationTypeName;
      inFlightInlineSavesRef.current += 1;
      try {
        await updateCrmAssociationAsync({
          object: objectRef.current,
          associationTypeName,
          targetIds: pickedIds,
          targetEntityTypeName,
          knownSubsetTargetIds,
        });
        if (pickedIds.length === 0) {
          setClearedAssociationKeys(prev =>
            prev.has(fieldKey) ? prev : new Set(prev).add(fieldKey)
          );
        }
        settleInlineSave(true);
      } catch (err) {
        settleInlineSave(false);
        throw err;
      }
    },
    [updateCrmAssociationAsync, settleInlineSave]
  );

  // Stage info for this object
  const stageEntityId = getStageEntityId(object, stageTypeNames);
  const stage = stageEntityId ? getStageByEntityId(stageEntityId) : undefined;

  // Determine if an association type points to a stage entity type
  const isStageAssociation = useCallback(
    (assocTypeName: string): boolean => {
      const targetEntityType = associationTypeMap.get(assocTypeName);
      return !!targetEntityType && stageTypeNames.has(targetEntityType);
    },
    [associationTypeMap, stageTypeNames]
  );

  // The create modal's fields stay on the panel as click-to-fill placeholders
  // when empty ("Set Email"), so the row set is stable from record to record
  // and an empty field is something to act on rather than a hole. Only
  // fields the user can actually fill here qualify — a placeholder that
  // ignores the click would be a lie — so the property half is intersected
  // with the inline-edit gate (canEdit + schema write access + an editor for
  // the field type) and the association half with its own gate below.
  const createFormKeys = useMemo(
    () =>
      getCreateFormFieldKeys({
        config,
        entityTypeName,
        schema,
        associationTypes,
        entityKindMap,
        stageTypeNames,
      }),
    [
      config,
      entityTypeName,
      schema,
      associationTypes,
      entityKindMap,
      stageTypeNames,
    ]
  );

  const placeholderPropertyKeys = useMemo((): ReadonlySet<string> => {
    if (!canEdit) {
      return EMPTY_KEY_SET;
    }
    const keys = new Set<string>();
    for (const key of createFormKeys.properties) {
      const propSchema = schema[key];
      if (
        propSchema &&
        isPropertyEditable(propSchema) &&
        hasPropertyEditor(propSchema.field_type_name)
      ) {
        keys.add(key);
      }
    }
    return keys;
  }, [canEdit, createFormKeys.properties, schema]);

  // Property rows kept in the list while empty: the placeholders above plus
  // any field cleared during this visit (see clearedPropertyKeys) — both in
  // their normal position, so the row never moves under the cursor.
  const keepEmptyPropertyKeys = useMemo((): ReadonlySet<string> => {
    if (clearedPropertyKeys.size === 0) {
      return placeholderPropertyKeys;
    }
    return new Set([...placeholderPropertyKeys, ...clearedPropertyKeys]);
  }, [placeholderPropertyKeys, clearedPropertyKeys]);

  // "View all values" means all of them: every property this record type
  // has, filled or not, so the expanded panel is the full picture of what
  // the record could hold. buildDetailFieldList still applies read access
  // and the registry's hidden list, so this only ever admits fields the
  // user could see filled.
  const expandedKeepEmptyPropertyKeys = useMemo(
    (): ReadonlySet<string> =>
      new Set([...keepEmptyPropertyKeys, ...Object.keys(schema)]),
    [keepEmptyPropertyKeys, schema]
  );

  // Association counterpart of placeholderPropertyKeys: the create modal
  // asks for it AND the user can set it here (permissions, schema write
  // access, and not write-once-frozen — an EMPTY write-once association is
  // still settable; `isAssociationValueFrozen` freezes it only once set).
  const placeholderAssociationKeys = useMemo((): ReadonlySet<string> => {
    if (!canEditAssociations) {
      return EMPTY_KEY_SET;
    }
    const keys = new Set<string>();
    for (const key of createFormKeys.associations) {
      const assocType = assocTypeByName.get(key);
      if (
        assocType &&
        assocType.value_write_access === 'all' &&
        !isAssociationValueFrozen(assocType, false)
      ) {
        keys.add(key);
      }
    }
    return keys;
  }, [canEditAssociations, createFormKeys.associations, assocTypeByName]);

  // Association rows kept in the list with no target: the placeholders
  // above plus any association cleared during this visit (see
  // clearedAssociationKeys) — the list otherwise only knows associations
  // present on the object.
  const keepEmptyAssociationKeys = useMemo((): ReadonlySet<string> => {
    if (clearedAssociationKeys.size === 0) {
      return placeholderAssociationKeys;
    }
    return new Set([...placeholderAssociationKeys, ...clearedAssociationKeys]);
  }, [placeholderAssociationKeys, clearedAssociationKeys]);

  // Build the ordered field lists using expandKindBasedAssociationFields so
  // kind-based rules render as one field per resolved target type.
  const baseFieldList = useMemo(
    (): DetailField[] =>
      expandKindBasedAssociationFields(
        buildDetailFieldList({
          mode: 'read',
          config,
          schema,
          object,
          assocAccessMap,
          associationTargetMap: associationTypeMap,
          entityKindMap,
          assocTypeByName,
          keepEmptyPropertyKeys,
          keepEmptyAssociationKeys,
        }),
        {
          assocTypeByName,
          objectTypes: allObjectTypes,
          presentTargetTypesByAssoc,
        }
      ),
    [
      config,
      schema,
      object,
      assocAccessMap,
      associationTypeMap,
      entityKindMap,
      assocTypeByName,
      keepEmptyPropertyKeys,
      keepEmptyAssociationKeys,
      allObjectTypes,
      presentTargetTypesByAssoc,
    ]
  );
  const fullFieldList = useMemo(
    (): DetailField[] =>
      expandKindBasedAssociationFields(
        buildDetailFieldList({
          mode: 'read',
          config,
          schema,
          object,
          assocAccessMap,
          associationTargetMap: associationTypeMap,
          entityKindMap,
          assocTypeByName,
          keepEmptyPropertyKeys: expandedKeepEmptyPropertyKeys,
          keepEmptyAssociationKeys,
        }),
        {
          assocTypeByName,
          objectTypes: allObjectTypes,
          presentTargetTypesByAssoc,
        }
      ),
    [
      config,
      schema,
      object,
      assocAccessMap,
      associationTypeMap,
      entityKindMap,
      assocTypeByName,
      expandedKeepEmptyPropertyKeys,
      keepEmptyAssociationKeys,
      allObjectTypes,
      presentTargetTypesByAssoc,
    ]
  );
  const fieldList = showAll ? fullFieldList : baseFieldList;

  const fieldSavers = useMemo(() => {
    const map = new Map<string, (v: unknown) => Promise<void>>();
    for (const field of fullFieldList) {
      if (field.type === 'property') {
        map.set(field.key, (v: unknown) => handleSaveProperty(field.key, v));
      } else if (field.type === 'association') {
        // Keyed by FIELD, not association type: two subsets of one kind-based
        // rule are two rows with two savers, each scoped to its target type.
        const { key, targetEntityTypeName } = field;
        // The ids THIS field rendered, so the scoped diff never has to guess a
        // rendered target's type from a cache that may have dropped it.
        const renderedIds = targetsForField(field).map(o => o.entity_id);
        map.set(detailFieldKey(field), (v: unknown) =>
          handleSaveAssociation(key, v, targetEntityTypeName, renderedIds)
        );
      }
    }
    return map;
  }, [
    fullFieldList,
    handleSaveProperty,
    handleSaveAssociation,
    targetsForField,
  ]);

  // The ✕ on a filled row: a save of the empty value through the same path
  // the editor's own clear takes (so the cleared-row bookkeeping above runs).
  const fieldClearers = useMemo(() => {
    const map = new Map<string, () => Promise<void>>();
    for (const field of fullFieldList) {
      if (field.type === 'property') {
        map.set(field.key, () => handleSaveProperty(field.key, null));
      } else if (field.type === 'association') {
        const { key, targetEntityTypeName } = field;
        const renderedIds = targetsForField(field).map(o => o.entity_id);
        map.set(detailFieldKey(field), () =>
          handleSaveAssociation(key, null, targetEntityTypeName, renderedIds)
        );
      }
    }
    return map;
  }, [
    fullFieldList,
    handleSaveProperty,
    handleSaveAssociation,
    targetsForField,
  ]);

  const handleRetry = useCallback(() => {
    if (schemaError) {
      refetchSchema();
    }
    refetchAssociations();
  }, [schemaError, refetchSchema, refetchAssociations]);

  // Whether we still need to wait before showing field rows. Schema is the
  // only critical dep — without it `fieldList` is empty and we have no
  // structure to render. Pipeline-stages and association-types fill in
  // inline (stage badges and association labels) and don't need to gate
  // the entire section on a 4-row gray skeleton block. Schema itself has
  // localStorage initialData backing so this is usually false even on a
  // cold session — meaning slideouts opened against a row already known
  // to the store render their overview rows immediately, with no flash.
  const hasSchemaLoadingFallback =
    schemaLoading && Object.keys(schema).length === 0;
  const hasFatalError =
    !!schemaError || (!!associationsError && !associationsPartialError);
  // Heading reads "{Type} overview" — for ahoy_note this is just "Note
  // overview" (no task / reminder discrimination). getTypeLabel(object)
  // is used over the name-only variant so future schema-driven type
  // overrides (the deferred ahoy_type_label property) drop in cleanly
  // without re-threading.
  const entityTypeLabel = getTypeLabel(
    object,
    {},
    entityTypeSchema ? [entityTypeSchema] : []
  );
  const title = titleOverride ?? `${entityTypeLabel} overview`;

  // Deferred switch (see inFlightInlineSavesRef above): queue rather than
  // fire immediately while an inline row save is still in flight.
  const startEditing = useCallback(() => {
    if (inFlightInlineSavesRef.current > 0) {
      pendingEditSwitchRef.current = true;
      return;
    }
    setIsEditing(true);
  }, []);

  // Header action toggles between "Edit" (enter edit mode) and "Cancel"
  // (exit edit mode). Cancel is needed in the header because the floating
  // SaveBar only renders when there are pending changes — without a
  // header Cancel, a user who entered edit mode but made no changes would
  // have no way out. When OverviewEditSection unmounts on exit, its
  // useFormState is GC'd so in-memory edits are discarded; the navigation
  // guard catches the unsaved-changes case at the surface level.
  const editAction = useMemo(() => {
    if (!canEdit) {
      return undefined;
    }
    if (isEditing) {
      return <SectionActionButton label='Cancel' onClick={handleExitEdit} />;
    }
    return (
      <SectionActionButton label='Edit' onClick={startEditing} icon={Edit01} />
    );
  }, [isEditing, canEdit, startEditing, handleExitEdit]);

  // Only show the section skeleton when schema is genuinely missing (cold
  // first-ever visit to this entity type, before localStorage has cached
  // it). In that case `fieldList` is empty and we have no structure to
  // render — a small skeleton block is a clearer affordance than "No
  // fields to display" briefly flashing. For warm sessions (the common
  // case), schema is in localStorage and this branch is skipped, letting
  // the section render immediately with whatever data is in `object`.
  if (hasSchemaLoadingFallback) {
    return (
      <OverviewSectionShell
        hideHeader={hideHeader}
        title={title}
        collapsible={collapsible}
        headerTrailing={headerTrailing}
      >
        <div className='space-y-3 pt-2' aria-busy='true'>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className='flex items-center gap-2'>
              <div className='w-5 h-5 bg-[var(--color-bg-tertiary)] rounded animate-pulse flex-shrink-0' />
              <div className='w-24 h-4 bg-[var(--color-bg-tertiary)] rounded animate-pulse flex-shrink-0' />
              <div className='flex-1 h-4 bg-[var(--color-bg-tertiary)] rounded animate-pulse' />
            </div>
          ))}
        </div>
      </OverviewSectionShell>
    );
  }

  if (hasFatalError && !isEditing) {
    return (
      <OverviewSectionShell
        hideHeader={hideHeader}
        title={title}
        collapsible={collapsible}
        headerTrailing={headerTrailing}
      >
        <QueryErrorState
          error={schemaError ?? associationsError}
          label="We couldn't load overview data"
          onRetry={handleRetry}
          size='sm'
        />
      </OverviewSectionShell>
    );
  }

  if (isEditing) {
    // Pass `actions={editAction}` so the header Cancel button renders while
    // editing. Without this, the early-return collapses the header to just
    // title + chevron and users have no way to exit edit mode until they've
    // made a change (the floating SaveBar's Cancel only appears with
    // pending edits). Without a header Cancel they perceive the Edit
    // affordance as "disappeared" — surfaced as "no Edit button" feedback
    // because the click looked like it broke something.
    return (
      <OverviewSectionShell
        hideHeader={hideHeader}
        title={title}
        actions={editAction}
        collapsible={collapsible}
        headerTrailing={headerTrailing}
      >
        <OverviewEditSection
          object={object}
          onSaveStateChange={onSaveStateChange ?? noopSaveState}
          onExitEdit={handleExitEdit}
        />
      </OverviewSectionShell>
    );
  }

  // Defer association rendering until the access map is populated. The
  // `internal` filter in buildDetailFieldList compares against
  // `assocAccessMap.get(field.key)` — when assoc types are still loading
  // the map is empty and `undefined !== 'internal'` so the row would slip
  // through. Hiding association rows until the map is ready keeps access
  // filtering consistent without gating the whole section on a skeleton.
  // localStorage `initialData` in `useAssociationTypes` makes this hidden
  // window vanish on warm sessions (the common case).
  // The object-type clause MUST fail closed: gate on the kind map actually
  // being populated, not on `!objectTypesQuery.isLoading`. React Query reports
  // `isLoading === false` after an error and while the query is disabled
  // (orgId undefined) — in both cases the kind map is still empty, so the
  // `!isLoading` form would declare access "ready" with an empty map and the
  // kind filter in `buildDetailFieldList` would silently drop every valid
  // party/case/asset/principal association. Requiring `entityKindMap.size > 0`
  // defers association rows until the map loads (warm sessions populate it
  // immediately via localStorage `initialData` in `useObjectTypes`).
  const associationsAccessReady =
    (!assocTypesLoading || associationTypes.length > 0) &&
    entityKindMap.size > 0;

  // Filter out association fields that would render empty or have dedicated sections
  const visibleFields = fieldList.filter(field => {
    if (field.type !== 'association') {
      return true;
    }
    if (!associationsAccessReady) {
      return false;
    }
    // Contact associations are rendered in dedicated PeopleSection (company/deal only)
    if (
      field.key.endsWith('_to_contact') &&
      (entityTypeName === 'ahoy_company' || entityTypeName === 'ahoy_deal')
    ) {
      return false;
    }
    // Integration association on email threads is rendered by
    // EmailThreadOverviewEnrichment (Source row); skip here to avoid duplicate.
    if (
      entityTypeName === 'ahoy_email_thread' &&
      field.key === 'ahoy_email_thread_to_integration'
    ) {
      return false;
    }
    if (isStageAssociation(field.key)) {
      return !!stage || placeholderAssociationKeys.has(field.key);
    }
    const objects = targetsForField(field);
    // Cleared associations would otherwise vanish under the cursor the
    // instant their target set hits zero — keep the row visible and
    // click-to-edit (see clearedAssociationKeys above). Create-form
    // associations stay as placeholders for the same reason, always.
    return (
      objects.length > 0 ||
      associationsLoading ||
      clearedAssociationKeys.has(detailFieldKey(field)) ||
      placeholderAssociationKeys.has(field.key)
    );
  });

  // Render a single detail field (property / association / timestamp). Shared
  // by both the always-visible "primary" rows and the collapsed "secondary"
  // rows so the rendering logic isn't duplicated. Returns null when the field
  // has no displayable value.
  const renderField = (field: DetailField): React.ReactNode => {
    if (field.type === 'property') {
      const propSchema = schema[field.key];
      if (!propSchema) {
        return null;
      }

      const ReadRenderer = config.customPropertyReadRenderers?.[field.key];
      if (ReadRenderer) {
        const rawValue = getPropertyValue<unknown>(
          object.properties?.[field.key]
        );
        return <ReadRenderer key={field.key} value={rawValue} orgId={orgId} />;
      }

      // JSON properties with a companion schema: decompose into
      // individual fields instead of rendering raw JSON.
      if (propSchema.field_type_name === 'json' && propSchema.schema_property) {
        const schemaValue = getPropertyValue<Record<string, unknown>>(
          object.properties?.[propSchema.schema_property]
        );
        const configValue = getPropertyValue<Record<string, unknown>>(
          object.properties?.[field.key]
        );

        // Fall back to raw JSON when companion schema is missing,
        // malformed, or lacks a `properties` definition — otherwise
        // JsonPropertyReadFields would render nothing and the value
        // would disappear from the overview entirely.
        const hasUsableSchema =
          schemaValue &&
          typeof schemaValue === 'object' &&
          !Array.isArray(schemaValue) &&
          'properties' in schemaValue;
        if (!hasUsableSchema && configValue) {
          return (
            <div key={field.key} className='flex items-start gap-2'>
              <span className='text-xs md:text-sm text-fg-quaternary w-28 flex-shrink-0'>
                {propSchema.label}
              </span>
              <pre className='text-body-small text-secondary whitespace-pre-wrap break-all'>
                {JSON.stringify(configValue, null, 2)}
              </pre>
            </div>
          );
        }

        return (
          <JsonPropertyReadFields
            key={field.key}
            schema={schemaValue as Record<string, unknown> | null}
            value={configValue as Record<string, unknown> | null}
            // A draft changing this JSON property diffs per sub-field rather
            // than as one blob. Without this the decomposed branch returns
            // before reaching the diff check below, so a proposal that edits
            // only config renders as if nothing had changed.
            previousValue={
              field.key in draftPreviousValues
                ? (draftPreviousValues[field.key] as Record<
                    string,
                    unknown
                  > | null)
                : null
            }
            orgId={orgId}
            sourceEntityTypeName={entityTypeName}
          />
        );
      }

      const IconComponent = getPropertyTypeIconComponent(propSchema);
      const value = getPropertyValue<unknown>(object.properties?.[field.key]);
      // The row owns copy: one control in its action cluster, revealed with
      // the row, directly right of the value — for text, email and phone
      // alike (see getRowCopyText for what qualifies). The value labels
      // that carry their own hover pill are told to render plain below, so
      // a row never shows two copy buttons or two hover surfaces.
      const copyText = getRowCopyText(value);
      const copyButton = copyText ? (
        <CopyButton
          text={copyText}
          label={propSchema.label}
          onCopy={handleCopyValue}
        />
      ) : undefined;

      if (field.key in draftPreviousValues) {
        // A pending proposal changes this property, so show the effect
        // rather than just the result. PropertyDiffValue is the record
        // timeline's own renderer, so each field type gets its proper
        // before/after treatment — currency as currency, dates as dates,
        // markdown as markdown — instead of degrading to strings. Always
        // read-only here: canEdit is false whenever a draft is on screen
        // (see canEdit above), so this branch never needs the inline editor.
        return (
          <OverviewFieldRow
            key={field.key}
            icon={IconComponent}
            label={propSchema.label}
            rowClassName='group'
            valueClassName='text-xs md:text-sm font-medium text-fg-primary flex-1'
            trailing={copyButton}
          >
            <PropertyDiffValue
              oldValue={draftPreviousValues[field.key]}
              newValue={value}
              schema={propSchema}
              orgId={orgId}
              entityProperties={object.properties}
            />
          </OverviewFieldRow>
        );
      }

      // Property gate: registry/permission/draft suppression (canEdit) AND
      // the schema permits a user write (isPropertyEditable) AND PropertyInput
      // actually has an editor for this field type (hasPropertyEditor) — the
      // read list doesn't filter on value_write_access, so formulas and
      // rollups reach here and must be excluded or PropertyInput would render
      // its "Unknown field type" box.
      const isEditableProperty =
        canEdit &&
        isPropertyEditable(propSchema) &&
        hasPropertyEditor(propSchema.field_type_name);
      const isEmpty = isEmptyValue(value);
      // ✕ clears a filled, editable, optional field. A required field can't
      // be emptied (the save would be rejected), so it gets no ✕.
      const clearer =
        isEditableProperty && !isEmpty && !propSchema.is_required
          ? fieldClearers.get(field.key)
          : undefined;

      // The copy affordance rides in the row's trailing slot, so the row can
      // hide it while its editor is open (the editor fills the value slot).
      return (
        <InlineEditableFieldRow
          key={field.key}
          kind='property'
          icon={IconComponent}
          label={propSchema.label}
          value={value}
          onSave={fieldSavers.get(field.key) as (v: unknown) => Promise<void>}
          onClear={clearer}
          isEditable={isEditableProperty}
          schema={propSchema}
          orgId={orgId}
          entityProperties={object.properties}
          valueClassName='text-xs md:text-sm font-medium text-fg-primary flex-1'
          trailing={copyButton}
        >
          {isEmpty ? (
            // Create-form / just-cleared fields, or any field once "View all
            // values" is open (see keepEmptyPropertyKeys and its expanded
            // twin); the placeholder is the click target when there's an
            // editor to open, the empty glyph when there isn't.
            <EmptyFieldPlaceholder
              label={propSchema.label}
              isEditable={isEditableProperty}
            />
          ) : (
            /* ExpandablePropertyValue preserves the long-value "Show
               more"/"Show less" clamp (unrelated to multi-value overflow
               — a single long text/markdown value needs it too), with
               ExpandableMultiValueLabel swapped in as the inner renderer
               so multi-select overflow ("+N") also collapses. Its
               resetKey is memoized internally, unlike the inlined version
               this replaced — this render function itself isn't a
               component and can't call hooks. */
            <CopyAffordanceContext value={copyButton ? 'host' : 'value'}>
              <ExpandablePropertyValue
                value={value}
                propertyType={propSchema}
                orgId={orgId}
                entityProperties={object.properties}
                ValueComponent={ExpandableMultiValueLabel}
              />
            </CopyAffordanceContext>
          )}
        </InlineEditableFieldRow>
      );
    }

    if (field.type === 'association') {
      // A decomposed field is named for the records it holds ("Deals",
      // "Tasks"), not the rule — the rule's own label describes every type of
      // its target kind at once, which is what made a contact's tasks read as
      // "Associated Deals".
      const label = field.targetEntityTypeName
        ? (typeLabelPluralMap.get(field.targetEntityTypeName) ??
          field.targetEntityTypeName)
        : (assocLabelMap.get(field.key) ?? field.key);

      // Stage association: render as Badge using stage label
      const AssocIcon = getAssociationTargetIconComponent(
        assocTypeByName.get(field.key) ?? {}
      );
      if (isStageAssociation(field.key)) {
        const stageBadge = stage ? (
          <Badge type='pill-color' color='brand' size='sm'>
            {stage.label}
          </Badge>
        ) : (
          // A pipelined record with no stage yet (visibleFields admits this
          // row only as a create-form placeholder): the stage picker, not
          // the entity picker, is the way to set it.
          <EmptyFieldPlaceholder label={label} />
        );
        const stageAssocType = assocTypeByName.get(field.key);
        if (!stageAssocType) {
          // No schema entry — render read-only, as before inline editing.
          return (
            <OverviewFieldRow
              key={field.key}
              icon={AssocIcon}
              label={label}
              valueClassName='flex-1 min-h-[22px]'
            >
              {stageBadge}
            </OverviewFieldRow>
          );
        }
        // Same gate as regular associations below: registry/draft/permission
        // suppression plus schema write access and the write-once freeze
        // check. A stage is never cleared from here (a pipelined record
        // always has one), so this row offers no ✕.
        const isEditableStage =
          canEditAssociations &&
          stageAssocType.value_write_access === 'all' &&
          !isAssociationValueFrozen(stageAssocType, !!stage);
        return (
          <InlineEditableFieldRow
            key={field.key}
            kind='stage'
            icon={AssocIcon}
            label={label}
            value={stageEntityId}
            onSave={fieldSavers.get(field.key) as (v: unknown) => Promise<void>}
            isEditable={isEditableStage}
            entityTypeName={entityTypeName}
            associationTypeName={field.key}
            valueClassName='flex-1 min-h-[22px]'
            // Editing an existing record's stage: never auto-pick the default
            // pipeline's first stage. The row commits on pick, so the stage
            // input's mount-time auto-select would silently save a stage the
            // user never chose the instant the picker opens (QA run 1 of
            // PR #14245: opening Lifecycle Stage wrote "Prospect" unbidden).
            disableAutoSelect
          >
            {stageBadge}
          </InlineEditableFieldRow>
        );
      }

      // Regular association: render as badges, diffed against any draft
      const objects = targetsForField(field);
      const fieldKey = detailFieldKey(field);
      const isPlaceholder = placeholderAssociationKeys.has(field.key);
      if (
        objects.length === 0 &&
        !associationsLoading &&
        !clearedAssociationKeys.has(fieldKey) &&
        !isPlaceholder
      ) {
        return null;
      }

      let badgeContent: ReactNode;
      if (associationsLoading && objects.length === 0) {
        badgeContent = (
          <EntityBadgeGroup
            state={{ kind: 'loading' }}
            variant='xs'
            loadingCount={1}
          />
        );
      } else if (objects.length === 0) {
        badgeContent = <EmptyFieldPlaceholder label={label} />;
      } else {
        badgeContent = (
          // Renders plain badges when there is no draft, and the added/removed
          // diff when a proposal touches this row's targets. Shared by BOTH the
          // read-only OverviewFieldRow fallback and the InlineEditableFieldRow
          // below, so a projected change is visible either way.
          <AssociationDiffValue
            entityIds={objects.map(o => o.entity_id)}
            changes={draftAssociationChanges}
            orgId={orgId}
          />
        );
      }

      const assocType = assocTypeByName.get(field.key);
      if (!assocType) {
        // No schema entry for this association type — render read-only,
        // exactly as before inline editing existed. Rare: assocTypeByName is
        // built from the same associationTypes the field list is derived from.
        return (
          <OverviewFieldRow
            key={field.key}
            icon={AssocIcon}
            label={label}
            valueClassName='flex flex-1 flex-wrap gap-1 min-h-[22px]'
          >
            {badgeContent}
          </OverviewFieldRow>
        );
      }

      // Association gate: schema write access, not currently frozen (a
      // DIFFERENT question from write access — see isAssociationValueFrozen's
      // docblock: write-once, not write-access), and both permissions —
      // replacing a target set both adds and removes edges. Shares canEdit's
      // registry/draft suppression via canEditAssociations.
      const isEditableAssociation =
        canEditAssociations &&
        assocType.value_write_access === 'all' &&
        !isAssociationValueFrozen(assocType, objects.length > 0);

      return (
        <InlineEditableFieldRow
          key={fieldKey}
          kind='association'
          icon={AssocIcon}
          label={label}
          value={objects.map(o => o.entity_id)}
          onSave={fieldSavers.get(fieldKey) as (v: unknown) => Promise<void>}
          onClear={
            isEditableAssociation && objects.length > 0
              ? fieldClearers.get(fieldKey)
              : undefined
          }
          isEditable={isEditableAssociation}
          associationType={assocType}
          // Scopes the picker's candidates to this field's type, so a "Deals"
          // row offers deals and never the record's tasks.
          targetEntityTypeName={field.targetEntityTypeName}
          orgId={orgId}
          stageTypeNames={stageTypeNames}
          sourceEntityId={object.entity_id}
          valueClassName='flex flex-1 flex-wrap gap-1 min-h-[22px]'
        >
          {badgeContent}
        </InlineEditableFieldRow>
      );
    }

    // Timestamp field (e.g. Created / Updated)
    const value = getPropertyValue<unknown>(object.properties?.[field.key]);
    const formatted = formatTimestamp(value, timeFormat);
    if (!formatted) {
      return null;
    }
    return (
      <OverviewFieldRow
        key={field.key}
        icon={Clock}
        label={getTimestampLabel(field.key)}
        valueClassName='text-xs text-fg-tertiary flex-1'
      >
        {formatted}
      </OverviewFieldRow>
    );
  };

  // What's filled always shows: the collapsed panel is every value the
  // record has, in order — nothing with data is ever behind the toggle.
  // "View all values" adds the record type's empty properties in their
  // natural position and, last, the created/updated timestamps (reference
  // data, not at-a-glance signal), and turns the header into a search over
  // the lot — by label, and by what the row shows: the value as rendered
  // (option labels, formatted phone and currency), the names of linked
  // records, the stage, the formatted timestamp. "acme" finds Works At as
  // readily as "works" does.
  const normalizedQuery = query.trim().toLowerCase();
  const fieldSearchTexts = (field: DetailField): string[] => {
    if (field.type === 'property') {
      const propSchema = schema[field.key];
      return [
        propSchema?.label ?? field.key,
        ...propertyValueSearchText(
          getPropertyValue<unknown>(object.properties?.[field.key]),
          propSchema
        ),
      ];
    }
    if (field.type === 'association') {
      const label = assocLabelMap.get(field.key) ?? field.key;
      if (isStageAssociation(field.key)) {
        return stage ? [label, stage.label] : [label];
      }
      return [label, ...associationSearchText(targetsForField(field))];
    }
    return [
      getTimestampLabel(field.key),
      formatTimestamp(
        getPropertyValue<unknown>(object.properties?.[field.key]),
        timeFormat
      ),
    ];
  };
  const matchesQuery = (field: DetailField): boolean =>
    matchesSearch(normalizedQuery, fieldSearchTexts(field));
  const dataRows = visibleFields.filter(
    field => field.type !== 'timestamp' && matchesQuery(field)
  );
  const timestampRows = showAll
    ? visibleFields.filter(
        field => field.type === 'timestamp' && matchesQuery(field)
      )
    : [];
  // The toggle exists whenever expanding would add a row: the timestamps,
  // or properties empty on this record.
  const hasMore =
    fullFieldList.length >
    baseFieldList.filter(field => field.type !== 'timestamp').length;
  const noMatches =
    showAll &&
    normalizedQuery !== '' &&
    dataRows.length === 0 &&
    timestampRows.length === 0;
  // Rows the toggle reveals fade in; rows that were already there don't
  // move and don't flicker (they keep their keys and stay mounted).
  const collapsedKeys = new Set(baseFieldList.map(field => field.key));
  const renderRow = (field: DetailField): ReactNode => {
    const row = renderField(field);
    if (!row || collapsedKeys.has(field.key)) {
      return row;
    }
    return (
      <div
        key={field.key}
        className='animate-in fade-in duration-200 motion-reduce:animate-none'
      >
        {row}
      </div>
    );
  };
  // Expanded, the header IS the search: a way back where the title's icon
  // would sit, the query where the title was. Borderless, so it reads as
  // the header itself rather than a form control dropped into it, and at
  // the header's own height so nothing below moves when it swaps in.
  const searchHeader = showAll ? (
    <>
      <Button
        color='link-gray'
        size='sm'
        iconLeading={ChevronLeft}
        aria-label='Back to overview'
        onClick={collapseAll}
        className='shrink-0'
      />
      <Input
        size='xs'
        icon={SearchLg}
        autoFocus
        aria-label='Search values'
        placeholder='Search values'
        value={query}
        onChange={setQuery}
        onKeyDown={handleSearchKeyDown}
        wrapperClassName='bg-transparent shadow-none ring-0'
        className='min-w-0 flex-1'
      />
    </>
  ) : undefined;

  return (
    <>
      <OverviewSectionShell
        hideHeader={hideHeader}
        title={title}
        actions={editAction}
        collapsible={collapsible}
        headerTrailing={headerTrailing}
        headerOverride={searchHeader}
      >
        {associationsPartialError && (
          <p className='text-xs md:text-sm text-fg-tertiary py-1'>
            Some associations failed to load.{' '}
            <button
              type='button'
              onClick={handleRetry}
              className='text-fg-secondary underline hover:no-underline cursor-pointer bg-transparent border-none p-0 text-xs md:text-sm'
            >
              Retry
            </button>
          </p>
        )}
        {visibleFields.length === 0 &&
        !schemaLoading &&
        entityTypeName !== 'ahoy_meeting' &&
        entityTypeName !== 'ahoy_meeting_notes' &&
        entityTypeName !== 'ahoy_email_thread' ? (
          // Don't flash this while schema is still loading — the user
          // might briefly see "No fields to display" then the real fields
          // pop in. Better to render an empty section briefly (the
          // CollapsibleSection title remains visible) and have fields
          // appear when schema arrives.
          <p className='text-xs md:text-sm text-fg-tertiary py-2'>
            {draft?.isUnsaved
              ? // Nothing is missing here — nothing has been described yet.
                // "No fields to display" would read as a record that came back
                // empty, rather than one that is waiting to be defined.
                'Describe what you want and the details will fill in here.'
              : 'No fields to display'}
          </p>
        ) : (
          // 12px between rows: with the 32px hover/edit cell drawn over a
          // 20–22px line (see InlineEditableFieldRow), a tighter rhythm has
          // one row's cell overlapping the next. 6px of vertical padding is
          // the same cell's bleed above the first row / below the last —
          // the accordion panel clips overflow, so anything less cuts the
          // top of the first editor off.
          <div id='overview-fields' className='space-y-3 py-1.5'>
            {/* Enrichment rows aren't searchable values; they step aside
                while a query is narrowing the list. */}
            {/* Meeting notes enrichment renders first (Meeting, Time, Attendees) */}
            {entityTypeName === 'ahoy_meeting_notes' && !normalizedQuery && (
              <MeetingNotesOverviewEnrichment object={object} />
            )}
            {/* Email thread enrichment renders first (Visibility, Source) */}
            {entityTypeName === 'ahoy_email_thread' && !normalizedQuery && (
              <EmailThreadOverviewEnrichment object={object} />
            )}
            {dataRows.map(renderRow)}
            {/* Enriched meeting data (deals via company, size, stage, deal value) */}
            {entityTypeName === 'ahoy_meeting' && !normalizedQuery && (
              <MeetingOverviewEnrichment object={object} />
            )}
            {timestampRows.map(renderRow)}
            {noMatches && (
              <p className='text-xs md:text-sm text-fg-tertiary'>
                No values match “{query.trim()}”
              </p>
            )}
            {hasMore && (
              <button
                ref={viewAllButtonRef}
                type='button'
                onClick={toggleShowAll}
                aria-expanded={showAll}
                aria-controls='overview-fields'
                className='mt-1 inline-flex w-fit rounded text-xs md:text-sm font-medium text-fg-tertiary hover:text-fg-secondary cursor-pointer bg-transparent border-none p-0 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
              >
                {showAll ? 'View fewer values' : 'View all values'}
              </button>
            )}
          </div>
        )}
      </OverviewSectionShell>
      {/* Section separator — only between the stacked sections in the slideout
          (collapsible). On the full-page record the overview is the sole
          section in the details rail, so this trailing line would just dangle
          at the bottom; drop it there. */}
      {collapsible && (
        <div className='border-t border-[var(--color-border-secondary)]' />
      )}
    </>
  );
}
